import { useMemo } from "react";

import { EmptystateVariablesColored } from "components/icons/generated";
import SortableTable, { SortableTableColumn } from "components/SortableTable";
import Button from "ds/components/Button";
import EmptyState from "ds/components/EmptyState";
import useTitle from "hooks/useTitle";
import useTypedContext from "hooks/useTypedContext";
import { ConfigElement, ConfigType, SearchQueryOrderDirection } from "types/generated";
import Tooltip from "ds/components/Tooltip";
import { TooltipReferenceProps } from "ds/components/Tooltip/types";
import PageInfoHeader from "ds/components/PageInfoHeader";

import { COLUMN_ORDER_VARIABLES } from "../constants";
import { ContextContext } from "../Context";
import { showContextVariablesFormDrawer } from "./FormDrawer";
import ContextVariablesListItem from "./ListItem";

const columns: SortableTableColumn[] = [
  {
    value: "id",
    label: "Name",
  },
  {
    value: "value",
    label: "Value",
    static: true,
  },
  {
    value: "createdAt",
    label: "Created at",
  },
];

const ContextVariables = () => {
  const { context, canUpdateContext } = useTypedContext(ContextContext);

  const { config } = context;

  useTitle(`Variables · ${context.name}`);

  const items = config.filter((item) => item.type === ConfigType.EnvironmentVariable);
  const existingNames = useMemo(() => items.map((item) => item.id), [items]);

  const handleEditClick = (variable: ConfigElement) => {
    showContextVariablesFormDrawer({ id: variable.id, variable, contextId: context.id });
  };

  const openCreateVariableDrawer = () => {
    showContextVariablesFormDrawer({ contextId: context.id, existingNames });
  };

  return (
    <>
      <PageInfoHeader title="Variables">
        <Tooltip
          on={(props: TooltipReferenceProps) => (
            <Button
              {...props}
              variant="primary"
              onPress={openCreateVariableDrawer}
              disabled={!canUpdateContext.result}
            >
              Add variable
            </Button>
          )}
          active={!!canUpdateContext.message}
        >
          {canUpdateContext.message}
        </Tooltip>
      </PageInfoHeader>

      {/* TODO: Add loading state */}
      {items.length !== 0 && (
        <SortableTable
          ariaLabel="Variables"
          items={items}
          columns={columns}
          columnOrder={COLUMN_ORDER_VARIABLES}
          renderItems={(sorted) => (
            <>
              {sorted.map((item) => (
                <ContextVariablesListItem key={item.id} item={item} onEdit={handleEditClick} />
              ))}
            </>
          )}
          initialSortBy="createdAt"
          initialDirection={SearchQueryOrderDirection.Desc}
        />
      )}

      {items.length === 0 && (
        <EmptyState
          padding="large 0"
          icon={EmptystateVariablesColored}
          title="You do not have any variables yet"
          caption="Environment variables are represented as a key-value mapping available to all processes running in a given environment. Click on the button below to create a new variable."
        />
      )}
    </>
  );
};

export default ContextVariables;
